import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type RefObject,
} from "react";

const TARGET_SAMPLE_RATE = 24000;
const HEYGEN_CHUNK_BYTES = 960;
const FRAME_SECONDS = 0.02;
const FRAME_INTERVAL_MS = 20;
const INITIAL_BUFFER_SECONDS = 1;
const INITIAL_BUFFER_FRAMES = Math.round(INITIAL_BUFFER_SECONDS / FRAME_SECONDS);
const MAX_BUFFER_FRAMES = INITIAL_BUFFER_FRAMES * 4;

export type DirectMicPlaybackStatus =
  | "idle"
  | "starting"
  | "live"
  | "error";

export function useDirectMicPlayback(
  enabled: boolean,
  sessionTargetRef: RefObject<unknown | null>,
) {
  const [status, setStatus] = useState<DirectMicPlaybackStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const samplesRef = useRef<number[]>([]);
  const frameQueueRef = useRef<string[]>([]);
  const initializedRef = useRef(false);
  const sendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const eventIdRef = useRef<string | null>(null);
  const lastSessionRef = useRef<unknown | null>(null);

  const stop = useCallback(() => {
    if (sendTimerRef.current) {
      clearInterval(sendTimerRef.current);
      sendTimerRef.current = null;
    }
    sendSpeakEnd(lastSessionRef.current ?? sessionTargetRef.current, eventIdRef.current);
    processorRef.current?.disconnect();
    sourceRef.current?.disconnect();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    void audioContextRef.current?.close();

    processorRef.current = null;
    sourceRef.current = null;
    streamRef.current = null;
    audioContextRef.current = null;
    samplesRef.current = [];
    frameQueueRef.current = [];
    eventIdRef.current = null;
    lastSessionRef.current = null;
    initializedRef.current = false;
    setIsStreaming(false);
    setStatus("idle");
  }, [sessionTargetRef]);

  const ensurePacedSender = useCallback(() => {
    if (sendTimerRef.current) return;

    const socket = getHeyGenSocket(sessionTargetRef.current);
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setStatus("error");
      setErrorMessage("HeyGen audio socket is not ready");
      return;
    }

    sendTimerRef.current = setInterval(() => {
      const frame = frameQueueRef.current.shift();
      if (!frame) return;

      if (frameQueueRef.current.length > MAX_BUFFER_FRAMES) {
        frameQueueRef.current.splice(
          0,
          frameQueueRef.current.length - MAX_BUFFER_FRAMES,
        );
      }

      const target = prepareCurrentTarget(
        sessionTargetRef.current,
        lastSessionRef,
        eventIdRef,
      );
      if (!target) return;

      sendPcmFrameToHeyGen(target.session, target.eventId, frame);
    }, FRAME_INTERVAL_MS);
  }, [sessionTargetRef]);

  const start = useCallback(async () => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setStatus("starting");
    setErrorMessage(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: getVoiceFirstAudioConstraints(),
      });
      await strengthenMicConstraints(stream);
      const AudioContextCtor =
        window.AudioContext ||
        (window as typeof window & { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!AudioContextCtor) {
        throw new Error("AudioContext is not supported in this browser");
      }
      const audioContext = new AudioContextCtor();
      await audioContext.resume();
      await waitForHeyGenSocket(sessionTargetRef.current);

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      const inputSampleRate = audioContext.sampleRate;
      const inputSamplesPerFrame = Math.round(inputSampleRate * FRAME_SECONDS);

      processor.onaudioprocess = (event) => {
        const input = event.inputBuffer.getChannelData(0);
        const output = event.outputBuffer.getChannelData(0);
        output.fill(0);

        samplesRef.current.push(...input);

        while (samplesRef.current.length >= inputSamplesPerFrame) {
          const frameSamples = samplesRef.current.splice(0, inputSamplesPerFrame);
          const frame = floatSamplesToPcm24kString(
            Float32Array.from(frameSamples),
            inputSampleRate,
          );

          if (frame) {
            frameQueueRef.current.push(frame);
            if (frameQueueRef.current.length >= INITIAL_BUFFER_FRAMES) {
              ensurePacedSender();
            }
          }
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      streamRef.current = stream;
      audioContextRef.current = audioContext;
      sourceRef.current = source;
      processorRef.current = processor;
      setIsStreaming(true);
      setStatus("live");
    } catch (error) {
      initializedRef.current = false;
      setIsStreaming(false);
      setStatus("error");
      setErrorMessage(
        error instanceof Error ? error.message : "Microphone failed to start",
      );
    }
  }, [ensurePacedSender, sessionTargetRef]);

  useEffect(() => {
    if (enabled) {
      void start();
    } else if (initializedRef.current) {
      stop();
    }
  }, [enabled, start, stop]);

  useEffect(() => stop, [stop]);

  const retry = useCallback(() => {
    stop();
    void start();
  }, [start, stop]);

  return {
    status,
    errorMessage,
    isStreaming,
    retry,
    stop,
  };
}

function getVoiceFirstAudioConstraints(): MediaTrackConstraints {
  return {
    autoGainControl: true,
    channelCount: { ideal: 1 },
    echoCancellation: true,
    noiseSuppression: true,
    sampleRate: { ideal: 48000 },
  };
}

async function strengthenMicConstraints(stream: MediaStream) {
  const [track] = stream.getAudioTracks();
  if (!track?.applyConstraints) return;

  await track
    .applyConstraints(getVoiceFirstAudioConstraints())
    .catch(() => undefined);
}

function sendPcmFrameToHeyGen(
  session: unknown | null,
  eventId: string | null,
  frame: string,
) {
  const socket = getHeyGenSocket(session);
  if (!eventId || socket?.readyState !== WebSocket.OPEN) return;

  socket.send(
    JSON.stringify({
      type: "agent.speak",
      event_id: eventId,
      audio: btoa(frame.slice(0, HEYGEN_CHUNK_BYTES)),
    }),
  );
}

function sendSpeakEnd(session: unknown | null, eventId: string | null) {
  const socket = getHeyGenSocket(session);
  if (!eventId || socket?.readyState !== WebSocket.OPEN) return;

  socket.send(JSON.stringify({ type: "agent.speak_end", event_id: eventId }));
}

function prepareCurrentTarget(
  session: unknown | null,
  lastSessionRef: MutableRefObject<unknown | null>,
  eventIdRef: MutableRefObject<string | null>,
) {
  if (!session) return null;

  if (session !== lastSessionRef.current) {
    sendSpeakEnd(lastSessionRef.current, eventIdRef.current);
    lastSessionRef.current = session;
    eventIdRef.current = createEventId();
  }

  if (!eventIdRef.current) {
    eventIdRef.current = createEventId();
  }

  return {
    session,
    eventId: eventIdRef.current,
  };
}

function createEventId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `mic_${Date.now()}`;
}

function getHeyGenSocket(session: unknown | null): WebSocket | null {
  if (!session || typeof session !== "object") return null;

  return (
    (session as { _sessionEventSocket?: WebSocket | null })._sessionEventSocket ??
    null
  );
}

async function waitForHeyGenSocket(session: unknown | null): Promise<WebSocket> {
  const deadline = Date.now() + 3000;

  while (Date.now() < deadline) {
    const socket = getHeyGenSocket(session);
    if (socket?.readyState === WebSocket.OPEN) return socket;
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  throw new Error("HeyGen audio socket is not ready");
}

function floatSamplesToPcm24kString(
  samples: Float32Array,
  inputSampleRate: number,
) {
  if (!samples.length) return "";

  const resampled = resampleLinear(samples, inputSampleRate, TARGET_SAMPLE_RATE);
  let binary = "";

  for (let i = 0; i < resampled.length; i += 1) {
    const clamped = Math.max(-1, Math.min(1, resampled[i] ?? 0));
    const value = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
    const int16 = Math.round(value);
    binary += String.fromCharCode(int16 & 0xff, (int16 >> 8) & 0xff);
  }

  return binary;
}

function resampleLinear(
  samples: Float32Array,
  inputSampleRate: number,
  outputSampleRate: number,
) {
  if (inputSampleRate === outputSampleRate) return samples;

  const ratio = inputSampleRate / outputSampleRate;
  const outputLength = Math.max(1, Math.round(samples.length / ratio));
  const output = new Float32Array(outputLength);

  for (let i = 0; i < outputLength; i += 1) {
    const sourceIndex = i * ratio;
    const before = Math.floor(sourceIndex);
    const after = Math.min(before + 1, samples.length - 1);
    const weight = sourceIndex - before;
    output[i] = (samples[before] ?? 0) * (1 - weight) + (samples[after] ?? 0) * weight;
  }

  return output;
}
